// Auto-extracted from Profile.jsx
import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { C } from "../../lib/theme";
import { Card } from "../../components/Card";
import { Btn } from "../../components/Btn";
import { Eyebrow } from "../../components/Eyebrow";
import { Icon } from "../../components/Icon";
import { Skeleton } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { ProductCard } from "../../components/ProductCard";
import { supabase } from "../../lib/supabase";
import { fmtK } from "../../lib/money";
import { useWishlist } from "../../store/wishlistStore";
import { getAllLocations } from "../../data/zambianLocations";
import { timeAgo } from "../../lib/dateUtils";
import { useCategories } from "../../hooks/useCategories";
import { BecomeSellerModal } from "../../components/BecomeSellerModal";
import { PhoneVerificationModal } from "../../components/PhoneVerificationModal";
import { DeleteAccountModal } from "../../components/DeleteAccountModal";
import { Field, SecurityRow, InfoRow, inputStyle } from "./shared";

export function PaymentMethodsPanel({ user }) {
  // Table is `payment_methods` (NOT `user_payment_methods`). Columns:
  // id, user_id, network, masked_number, is_default, is_verified, created_at.
  const queryClient = useQueryClient();
  const queryKey = ["payment-methods", user?.id];
  const [error, setError] = useState(null);

  const { data: items = [], isLoading: loading } = useQuery({
    queryKey,
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error: err } = await supabase
        .from("payment_methods")
        .select("id, network, masked_number, is_default, is_verified, created_at")
        .eq("user_id", user.id)
        .order("is_default", { ascending: false })
        .order("created_at", { ascending: false });
      if (err) {
        setError(err.message);
        throw err;
      }
      return data || [];
    },
  });

  async function remove(id) {
    await supabase.from("payment_methods").delete().eq("id", id);
    queryClient.setQueryData(queryKey, (prev = []) => prev.filter((i) => i.id !== id));
  }

  async function setDefault(id) {
    await supabase.from("payment_methods").update({ is_default: false }).eq("user_id", user.id);
    await supabase.from("payment_methods").update({ is_default: true }).eq("id", id);
    queryClient.setQueryData(queryKey, (prev = []) => prev.map((m) => ({ ...m, is_default: m.id === id })));
  }

  const networkLabel = { airtel: "Airtel Money", mtn: "MTN MoMo", visa: "Visa", mastercard: "Mastercard" };
  const networkIcon = { airtel: "smartphone", mtn: "smartphone", visa: "credit-card", mastercard: "credit-card" };

  return (
    <Card pad={22}>
      <Eyebrow>Saved payment methods</Eyebrow>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
        Methods are saved automatically when you check out. They are read-only here for security — remove any you no longer use.
      </div>
      {loading ? <div style={{ marginTop: 14 }}><Skeleton h={60} /></div> : items.length === 0 ? (
        <div style={{ padding: "16px 0", fontSize: 12.5, color: C.muted }}>No saved methods yet.</div>
      ) : (
        <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
          {items.map((m) => (
            <div key={m.id} style={{
              padding: 14,
              borderRadius: 10,
              border: m.is_default ? `2px solid ${C.hunter}` : "1px solid rgba(196,193,183,0.35)",
              background: m.is_default ? C.cream : C.white,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}>
              <span style={{ width: 40, height: 40, borderRadius: 10, background: C.white, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name={networkIcon[m.network] || "credit-card"} size={16} color={C.hunter} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: C.charcoal, display: "flex", alignItems: "center", gap: 8 }}>
                  {networkLabel[m.network] || m.network}
                  {m.is_default && <span style={{ fontSize: 9.5, padding: "2px 6px", borderRadius: 6, background: C.hunter, color: C.dust, fontWeight: 800, letterSpacing: 0.5 }}>DEFAULT</span>}
                  {m.is_verified && <span style={{ fontSize: 9.5, padding: "2px 6px", borderRadius: 6, background: C.greenBg, color: C.greenDk, fontWeight: 800, letterSpacing: 0.5 }}>VERIFIED</span>}
                </div>
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                  {m.masked_number || "—"}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {!m.is_default && <Btn size="sm" kind="outline" onClick={() => setDefault(m.id)}>Default</Btn>}
                <button onClick={() => remove(m.id)} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 6 }}>
                  <Icon name="trash-2" size={14} color={C.red} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {error && <div style={{ marginTop: 10, fontSize: 12, color: C.red, fontWeight: 700 }}>{error}</div>}
    </Card>
  );
}
